import type {SketchPointObject} from './SketchPointObject';
import type {ISketchObject} from './ISketchObject';
import type {SketchRelationDefinition} from '../../sketch/solver/constraint/relationDefinition/SketchRelationDefinition';

/**
 * 草图复制上下文。
 * 说明：
 * 1. 复制复合对象（如集合、槽口、草图组）时，多个边对象可能共享同一个点对象；
 * 2. 为保持“新对象内部拓扑共享关系不变”，需要在一次复制流程中复用同一个点副本；
 * 3. 该上下文仅用于复制阶段，不参与运行时约束求解。
 */
export interface SketchCloneContext {
    /** 旧点 UUID 到新点对象的映射。 */
    pointMap: Map<string, SketchPointObject>;
    /** 旧草图对象 UUID 到新草图对象的映射。 */
    objectMap: Map<string, ISketchObject>;
    /**
     * 约束复制准入策略。
     *
     * 未提供时复制全部合法关系，用于历史快照表达同一逻辑草图；创建新逻辑实体时，
     * 调用方可排除尺寸等领域主状态生成的运行时派生关系，避免继承旧领域身份。
     */
    shouldCloneConstraint?: (constraint: SketchRelationDefinition<ISketchObject>) => boolean;
    /**
     * 点材质克隆模式。
     *
     * 仅供不挂场景的瞬时几何快照使用；正式实体复制保持默认工厂规范材质。
     */
    reusePointMaterial?: boolean;
}

/**
 * 创建草图复制上下文。
 *
 * @param shouldCloneConstraint 可选约束复制准入策略。
 * @param reusePointMaterial 瞬时快照是否复用源点共享材质；正式实体复制必须保持 false。
 * @returns 初始化后的复制上下文。
 */
export function createSketchCloneContext(
    shouldCloneConstraint?: (constraint: SketchRelationDefinition<ISketchObject>) => boolean,
    reusePointMaterial = false,
): SketchCloneContext {
    return {
        pointMap: new Map<string, SketchPointObject>(),
        objectMap: new Map<string, ISketchObject>(),
        ...(shouldCloneConstraint ? {shouldCloneConstraint} : {}),
        ...(reusePointMaterial ? {reusePointMaterial: true} : {}),
    };
}
